
import { useState, useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import { OnboardingFormValues } from "../OnboardingFormSchema";
import { useWizard } from "../FormWizardContext";
import { Button } from "@/components/ui/button";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";
import { AlertCircle, CalendarIcon, CheckCircle, Loader2, ExternalLink, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface InvoiceGenerationStepProps {
  form: UseFormReturn<OnboardingFormValues>;
  formId?: string;
}

export const InvoiceGenerationStep = ({ form, formId: propFormId }: InvoiceGenerationStepProps) => {
  const { currentStep, totalSteps } = useWizard();
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationCompleted, setGenerationCompleted] = useState(false);
  const [invoiceUrl, setInvoiceUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedChain, setSelectedChain] = useState<"L1" | "L2">("L1");
  
  // Get the token type from previous form
  const tokenType = form.getValues("currency_type");
  // Use the formId from props first, if not available get it from form values if it exists
  const formIdValue = propFormId || form.watch("pic_id");
  
  useEffect(() => {
    // Set chain based on token type
    if (tokenType) {
      const defaultChain = tokenType === "MNT" ? form.getValues("chain") || "L1" : "L1";
      setSelectedChain(defaultChain as "L1" | "L2");
      form.setValue("chain", defaultChain as "L1" | "L2");
    }
    
    // Set network based on token type
    if (tokenType && !form.getValues('wallet_address_network')) {
      if (tokenType === "MNT") {
        form.setValue('wallet_address_network', "Mantle Network(MNT)");
      } else if (tokenType === "USDC") {
        form.setValue('wallet_address_network', "USDC(ERC20)");
      } else if (tokenType === "USDT") {
        form.setValue('wallet_address_network', "USDT(ERC20)");
      }
    }

    // Check if we already have an invoice URL - important for step return visits
    const existingInvoiceUrl = form.getValues('invoice_link');
    if (existingInvoiceUrl) {
      setInvoiceUrl(existingInvoiceUrl);
      setGenerationCompleted(true);
    }
  }, [form, tokenType]);
  
  // Check if this step should be rendered based on current step and total steps
  if (!(
    (totalSteps === 2 && currentStep === 1) || // Phase 3: step 1 of 2
    (totalSteps === 5 && currentStep === 4)    // Other phases: step 4 of 5
  )) {
    return null;
  }

  const values = form.getValues();
  const today = new Date();
  const invoiceNumber = values.invoice_number || `INV-${Math.floor(Math.random() * 10000)}-${format(today, 'yyyyMMdd')}`;

  // Determine which network options should be available based on token type
  const getNetworkOptions = () => {
    if (tokenType === "MNT") {
      return [{ value: "Mantle Network(MNT)", label: "Mantle Network(MNT)" }];
    } else if (tokenType === "USDC") {
      return [{ value: "USDC(ERC20)", label: "USDC(ERC20)" }];
    } else if (tokenType === "USDT") {
      return [{ value: "USDT(ERC20)", label: "USDT(ERC20)" }];
    } else {
      return [
        { value: "Mantle Network(MNT)", label: "Mantle Network(MNT)" },
        { value: "USDC(ERC20)", label: "USDC(ERC20)" },
        { value: "USDT(ERC20)", label: "USDT(ERC20)" }
      ];
    }
  };

  // Set default values if not already set
  if (!form.getValues('invoice_number')) {
    form.setValue('invoice_number', invoiceNumber);
  }

  if (!form.getValues('invoice_date')) {
    form.setValue('invoice_date', today);
  }

  if (!form.getValues('amount') && values.agreed_rate) {
    const agreed_rate = typeof values.agreed_rate === 'string' 
      ? parseFloat(values.agreed_rate) 
      : values.agreed_rate;
      
    if (!isNaN(agreed_rate)) {
      form.setValue('amount', agreed_rate);
    }
  }

  const handleGenerateInvoice = async () => {
    try {
      setError(null);
      setInvoiceUrl(null); // Reset invoice URL if re-generating
      
      const fieldsToValidate = [
        'invoice_number',
        'invoice_date',
        'wallet_address',
        'wallet_address_network',
        'chain',
        'amount',
        'description',
      ];
      
      const isValid = await form.trigger(fieldsToValidate as any);
      if (!isValid) {
        toast({
          title: "Validation Error",
          description: "Please fill in all required invoice fields.",
          variant: "destructive"
        });
        return;
      }
      
      // Get the amount for the invoice generation
      const amount = form.getValues('amount');
      console.log("Using amount for invoice:", amount);
      
      // Set total to match amount to ensure consistency
      form.setValue('total', amount);
      
      const invoiceData = {
        invoice_number: form.getValues('invoice_number'),
        invoice_date: format(form.getValues('invoice_date') || new Date(), 'yyyy-MM-dd'),
        name: form.getValues('creator_name'),
        wallet_address: form.getValues('wallet_address'),
        wallet_address_network: form.getValues('wallet_address_network'),
        chain: form.getValues('chain'),
        amount: form.getValues('amount'),
        total: form.getValues('amount'), // For invoice generation they need to match
        description: form.getValues('description'),
      };

      console.log("Generating invoice with data:", invoiceData);
      setIsGenerating(true);

      // Call the edge function to generate the invoice
      const { data, error } = await supabase.functions.invoke('generate-invoice', {
        body: { invoiceData },
      });

      if (error) {
        console.error("Error invoking function:", error);
        throw new Error(error.message);
      }

      if (data) {
        console.log("Invoice generation response:", data);
        
        if (data.error) {
          throw new Error(data.error);
        }

        // Set the invoice URL in the form and state
        if (data.invoiceUrl) {
          // Store the original URL in the form for submission
          const originalUrl = data.invoiceUrl;
          
          // Set both URLs in the form - original URL for submission
          form.setValue('invoice_link', originalUrl);
          form.setValue('document_url', originalUrl);
          
          // Create a unique timestamp to ensure we always get a fresh PDF
          const uniqueId = Date.now() + Math.random().toString(36).substring(2, 9);
          
          // Add uniqueId parameter to the URL to force refresh of the PDF
          const urlWithTimestamp = originalUrl.includes('?') 
            ? `${originalUrl}&uid=${uniqueId}` 
            : `${originalUrl}?uid=${uniqueId}`;
          
          // But use uniqueId URL for preview to ensure it refreshes
          setInvoiceUrl(urlWithTimestamp);
          setGenerationCompleted(true);
          
          toast({
            title: "Invoice Generated",
            description: "Your invoice has been created successfully. Please click 'Next to Review' to continue.",
          });
        } else {
          throw new Error("No invoice URL returned from the function");
        }
      }
    } catch (error: any) {
      console.error("Error generating invoice:", error);
      setError(error.message || "Failed to generate invoice. Please try again.");
      toast({
        title: "Invoice Generation Failed",
        description: error.message || "Failed to generate invoice. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleChainSelect = (chainType: "L1" | "L2") => {
    setSelectedChain(chainType);
    form.setValue("chain", chainType);
  };
  
  // Function to update amount and sync with total
  const handleAmountChange = (val: string) => {
    // Handle empty string or parse as float
    if (val === '') {
      form.setValue('amount', undefined);
    } else {
      const numericValue = parseFloat(val);
      if (!isNaN(numericValue)) {
        form.setValue('amount', numericValue);
        // Also sync total with amount for consistency
        form.setValue('total', numericValue);
        console.log(`Set amount to: ${numericValue} and synchronized total`);
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Payment Invoice</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Generate an invoice for content creator payment.
        </p>
      </div>
      
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {/* Show generated invoice success box with clearer next steps guidance */}
      {invoiceUrl && generationCompleted && (
        <Alert variant="default" className="mb-4 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
          <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-500" />
          <AlertDescription className="flex flex-col space-y-2">
            <p className="font-medium">Invoice generated successfully!</p>
            <div className="flex items-center gap-2">
              <a 
                href={invoiceUrl} 
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline flex items-center"
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                View Invoice
              </a>
            </div>
            <div className="mt-2 flex justify-center">
              <iframe 
                src={invoiceUrl} 
                title="Invoice Preview" 
                className="w-full h-64 md:h-96 border border-gray-200 dark:border-gray-700 rounded"
              />
            </div>
            <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-md border border-blue-200 dark:border-blue-800 flex items-center">
              <ArrowRight className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2 flex-shrink-0" />
              <p className="text-sm text-blue-700 dark:text-blue-300">
                <strong>Next Step:</strong> Please click the "Next to Review" button at the bottom to continue to the review page.
              </p>
            </div>
          </AlertDescription>
        </Alert>
      )}
      
      {/* Improved horizontal alignment - using grid with same columns for both rows */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* First row: Invoice Number and Invoice Date - now properly aligned */}
        <FormField
          control={form.control}
          name="invoice_number"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Invoice Number <span className="text-red-500">*</span></FormLabel>
              <FormControl>
                <Input {...field} required />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="invoice_date"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Invoice Date <span className="text-red-500">*</span></FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      className="w-full pl-3 text-left font-normal flex justify-between"
                    >
                      {field.value ? (
                        format(field.value, "PPP")
                      ) : (
                        <span>Pick a date</span>
                      )}
                      <CalendarIcon className="ml-auto h-4 w-4" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={field.value}
                    onSelect={field.onChange}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />
        
        {/* Second row: Wallet Address and Wallet Address Network */}
        <FormField
          control={form.control}
          name="wallet_address"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Wallet Address <span className="text-red-500">*</span></FormLabel>
              <FormControl>
                <Input {...field} placeholder="0x..." required />
              </FormControl>
              <FormDescription>
                Enter the wallet address for receiving payment
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="wallet_address_network"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Wallet Address Network <span className="text-red-500">*</span></FormLabel>
              <Select 
                onValueChange={field.onChange} 
                value={field.value}
                disabled={tokenType === "MNT" || tokenType === "USDC" || tokenType === "USDT"}
                required
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select network" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {/* Dynamic network options based on token type */}
                  {getNetworkOptions().map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>
                {tokenType ? 
                  `Network automatically selected for ${tokenType} token type` :
                  "Select the blockchain network for the wallet address"}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        
        {/* Chain Type (now with badges) and Amount */}
        <FormField
          control={form.control}
          name="chain"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <FormLabel>Chain Type <span className="text-red-500">*</span></FormLabel>
              <FormControl>
                <div className="flex space-x-2">
                  <Badge 
                    variant={selectedChain === "L1" ? "default" : "outline"} 
                    className={`cursor-pointer px-4 py-2 ${selectedChain === "L1" ? "" : "hover:bg-gray-100 dark:hover:bg-gray-800"}`}
                    onClick={() => handleChainSelect("L1")}
                  >
                    L1
                  </Badge>
                  <Badge 
                    variant={selectedChain === "L2" ? "default" : "outline"} 
                    className={`cursor-pointer px-4 py-2 ${selectedChain === "L2" ? "" : "hover:bg-gray-100 dark:hover:bg-gray-800"}`}
                    onClick={() => handleChainSelect("L2")}
                    // Disable L2 for non-MNT tokens
                    style={{ opacity: tokenType !== "MNT" ? 0.5 : 1, pointerEvents: tokenType !== "MNT" ? "none" : "auto" }}
                  >
                    L2
                  </Badge>
                </div>
              </FormControl>
              <FormDescription>
                {tokenType !== "MNT" 
                  ? "Only L1 chain is available for ERC20 tokens" 
                  : "Select the appropriate blockchain layer"}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="amount"
          render={({ field: { onChange, value, ...rest } }) => (
            <FormItem>
              <FormLabel>Amount <span className="text-red-500">*</span></FormLabel>
              <FormControl>
                <Input
                  {...rest}
                  type="number"
                  value={value ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    onChange(val);
                    handleAmountChange(val);
                  }}
                  required
                />
              </FormControl>
              <FormDescription>
                Enter the payment amount in selected currency
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        
        {/* New Description field - takes full width */}
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem className="md:col-span-2">
              <FormLabel>Description <span className="text-red-500">*</span></FormLabel>
              <FormControl>
                <Textarea 
                  {...field} 
                  placeholder="Enter payment description or additional information" 
                  className="resize-none"
                  required
                />
              </FormControl>
              <FormDescription>
                Provide details about the services or deliverables this invoice covers
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      
      <div className="flex justify-center pt-4">
        <Button
          type="button"
          onClick={handleGenerateInvoice}
          disabled={isGenerating}
          className="min-w-[200px]"
        >
          {isGenerating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Generating Invoice...
            </>
          ) : (
            generationCompleted ? "Regenerate Invoice" : "Generate Invoice"
          )}
        </Button>
      </div>
    </div>
  );
};
